import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
    getDashboardBetaStats,
    subscribeToCompanyActivity,
    unsubscribeFromCompanyActivity,
} from "../../lib/supabase/dashboard-beta";

import { FaBoxesStacked, FaPlus } from "react-icons/fa6";
import { FaBoxOpen } from "react-icons/fa";
import { RiCalendarScheduleFill, RiUserSettingsLine } from "react-icons/ri";
import { MdManageAccounts } from "react-icons/md";
import {
    FiAlertTriangle, FiArrowRight, FiBarChart2, FiCalendar, FiCheckCircle,
    FiDollarSign, FiRefreshCw, FiSend, FiTrendingUp,
    FiUsers, FiZap,
} from "react-icons/fi";
import { Truck } from "lucide-react";

import Skeleton from "react-loading-skeleton";
import { hasPermission } from "../../lib/permissions";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const timeAgo = (date) => {
    if (!date) return "";
    const d = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (d < 60) return "just now";
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    if (d < 604800) return `${Math.floor(d / 86400)}d ago`;
    return new Date(date).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
};

const formatMoneyCompact = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "—";

    return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        notation: amount >= 100000 ? "compact" : "standard",
    }).format(amount);
};

const actionLabel = (action) => (action ? String(action).replace(/_/g, " ") : "did something");

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_META = {
    pending_design: { label: "Pending Design", dot: "bg-blue-400", bar: "bg-blue-400", bg: "bg-blue-50", text: "text-blue-600" },
    pending_estimation: { label: "Pending Estimate", dot: "bg-violet-400", bar: "bg-violet-400", bg: "bg-violet-50", text: "text-violet-600" },
    in_progress: { label: "In Progress", dot: "bg-amber-400", bar: "bg-amber-400", bg: "bg-amber-50", text: "text-amber-600" },
    ready_for_delivery: { label: "Ready for Delivery", dot: "bg-teal-400", bar: "bg-teal-400", bg: "bg-teal-50", text: "text-teal-600" },
    completed: { label: "Completed", dot: "bg-gray-400", bar: "bg-gray-400", bg: "bg-gray-100", text: "text-gray-500" },
};

const JOB_TYPE_LABEL = {
    Design: "Design",
    Estimation: "Estimate",
    "Design & Estimation": "D & E",
    Timber: "Timber",
};

const TONE_STYLES = {
    default: "bg-gray-100 text-gray-500",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    blue: "bg-blue-50 text-blue-600",
};

const EMPTY_STATS = {};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">{children}</p>
);

const LivePill = () => (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
        <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
        </span>
        <span className="text-[11px] font-semibold text-emerald-500">Live</span>
    </div>
);

const HeroStat = ({ label, value, Icon, accent = "text-amber-400", loading, isText }) => (
    <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Icon className={`w-4 h-4 ${accent}`} />
        </div>
        <div className="min-w-0">
            <div className="text-2xl sm:text-3xl font-bold text-white tabular-nums leading-none tracking-tight">
                {loading ? (
                    <Skeleton width={64} height={30} baseColor="#3f3f46" highlightColor="#52525b" />
                ) : isText ? (
                    value
                ) : (
                    (value ?? 0).toLocaleString()
                )}
            </div>
            <div className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mt-1.5">{label}</div>
        </div>
    </div>
);

const StatCard = ({ label, value, Icon, tone = "default", loading }) => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${TONE_STYLES[tone]}`}>
            <Icon className="w-4 h-4" />
        </div>
        <div>
            <div className="text-2xl font-bold text-zinc-700 leading-none tabular-nums">
                {loading ? <Skeleton width={36} height={26} /> : (value ?? 0).toLocaleString()}
            </div>
            <div className="text-[11px] text-gray-400 mt-1">{label}</div>
        </div>
    </div>
);

const QuickActionPill = ({ Icon, label, onClick }) => (
    <button
        onClick={onClick}
        title={label}
        className="group flex items-center gap-1.5 pl-1.5 pr-3 py-1.5 rounded-full bg-white border border-gray-200 shadow-sm hover:border-amber-300 hover:shadow transition-all"
    >
        <span className="w-6 h-6 rounded-full bg-zinc-800 group-hover:bg-amber-500 flex items-center justify-center flex-shrink-0 transition-colors">
            <Icon size={11} className="text-white" />
        </span>
        <span className="text-xs font-semibold text-zinc-700 whitespace-nowrap">{label}</span>
    </button>
);

const Sparkline = ({ data = [] }) => {
    const max = Math.max(...data.map((d) => d.count), 1);

    return (
        <div className="flex items-end gap-[3px] h-12">
            {data.map((point, index) => {
                const isLast = index === data.length - 1;
                const heightPct = Math.max((point.count / max) * 100, point.count > 0 ? 10 : 4);

                return (
                    <div
                        key={point.date}
                        title={`${point.date}: ${point.count} job${point.count !== 1 ? "s" : ""}`}
                        className={`flex-1 rounded-t-sm transition-all ${isLast ? "bg-amber-500" : "bg-amber-200"}`}
                        style={{ height: `${heightPct}%` }}
                    />
                );
            })}
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminDashboardBeta = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const { companySlug } = useParams();

    const [stats, setStats] = useState(EMPTY_STATS);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    const loadStats = useCallback(async (isRefresh = false) => {
        if (!company?.id) return;

        try {
            if (isRefresh) setRefreshing(true);
            else setLoading(true);

            const data = await getDashboardBetaStats(company.id);
            setStats(data);
            setLastUpdated(new Date());
        } catch (err) {
            console.error("Failed to fetch beta dashboard stats:", err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [company?.id]);

    useEffect(() => {
        loadStats(false);
    }, [loadStats]);

    useEffect(() => {
        if (!company?.id) return undefined;

        const channel = subscribeToCompanyActivity(company.id, (payload) => {
            const row = payload.new;
            if (!row) return;

            setStats((prev) => ({
                ...prev,
                recentActivity: [row, ...(prev.recentActivity || [])].slice(0, 15),
            }));
        });

        return () => {
            unsubscribeFromCompanyActivity(channel);
        };
    }, [company?.id]);

    const go = (route) => navigate(`/${companySlug}/${route}`);

    const firstName = capitalize(
        userProfile?.full_name?.split(" ")[0] || userProfile?.username || ""
    );

    const relativeTime = lastUpdated
        ? (() => {
            const d = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);
            if (d < 30) return "just now";
            if (d < 60) return `${d}s ago`;
            if (d < 3600) return `${Math.floor(d / 60)}m ago`;
            return `${Math.floor(d / 3600)}h ago`;
        })()
        : null;

    const maxBreakdownCount = Math.max(...((stats.statusBreakdown || []).map((s) => s.count)), 1);
    const trendTotal = (stats.jobsTrend || []).reduce((sum, d) => sum + d.count, 0);

    const ACTIONS = [
        {
            id: "new",
            roles: ["super_admin", "admin"],
            route: "assignment/new",
            Icon: FaPlus,
            label: "New Assignment",
            desc: "Create and assign new jobs",
        },
        {
            id: "my-asgn",
            roles: ["designer", "estimator"],
            permissions: ["SEEPAGE_MYJOBS"],
            route: "assignment/my-assignments",
            Icon: FaBoxOpen,
            label: "My Assignments",
            desc: "Monitor your assignments",
        },
        {
            id: "my-del",
            roles: ["driver"],
            route: "my-deliveries",
            Icon: Truck,
            label: "My Deliveries",
            desc: "View active deliveries",
        },
        {
            id: "track",
            roles: ["super_admin", "admin", "designer", "estimator"],
            route: "assignment/list",
            Icon: FaBoxesStacked,
            label: "Track Assignments",
            desc: "Monitor all active jobs",
        },
        {
            id: "customers",
            roles: ["super_admin", "admin", "designer", "estimator"],
            route: "customers",
            Icon: FiUsers,
            label: "Customers",
            desc: "Create and organize customers",
        },
        {
            id: "schedule",
            roles: ["super_admin", "admin", "designer", "estimator"],
            route: "schedule",
            Icon: RiCalendarScheduleFill,
            label: "Schedule",
            desc: "Plan and organize tasks",
        },
        {
            id: "users",
            roles: ["super_admin", "admin", "designer", "estimator", "driver"],
            route: "users",
            Icon: RiUserSettingsLine,
            label: "Users",
            desc: hasPermission(userProfile?.role, ["super_admin", "admin"]) ? "Manage your team" : "View team members",
        },
        {
            id: "analytics",
            roles: ["super_admin", "admin"],
            route: "analytics",
            Icon: FiBarChart2,
            label: "Analytics",
            desc: "Reports and insights",
        },
        {
            id: "final-pricings",
            roles: [],
            permissions: ["view_finalpricing_page"],
            route: "final-pricings",
            Icon: FiDollarSign,
            label: "Final Pricings",
            desc: "Review and enter final job prices",
        },
        {
            id: "final-pricing-calendar",
            roles: [],
            permissions: ["view_finalpricing_page"],
            route: "calendar/final-pricing",
            Icon: FiCalendar,
            label: "Final Pricing Calendar",
            desc: "View final pricing by delivery date",
        },
        {
            id: "quotations",
            roles: [],
            permissions: ["view_quotation_page"],
            route: "quotations",
            Icon: FiSend,
            label: "Quotation",
            desc: "Email final pricing quotes to customers",
        },
    ];

    const isActionVisible = (a) => {
        const roles = a.roles || [];
        const perms = a.permissions || [];
        if (roles.length === 0 && perms.length === 0) return true;
        const roleOk = roles.length > 0 && hasPermission(userProfile?.role, roles);
        const permOk = perms.length > 0 && perms.some((p) => userProfile?.permissions?.includes(p));
        return roleOk || permOk;
    };
    const ACTIONS_VISIBLE = ACTIONS.filter(isActionVisible);

    const hasAttentionItems =
        (stats.missingPricingJobs ?? 0) > 0 ||
        (stats.notYetQuotedJobs ?? 0) > 0 ||
        (stats.urgentJobsList || []).length > 0;

    return (
        <div className="mobile-dashboard-page beta-dashboard-page flex flex-col gap-8">

            {/* ── Hero ─────────────────────────────────────────────────────── */}
            <div className="-mx-4 sm:-mx-8 -mt-8 px-4 sm:px-8 pt-7 pb-9 relative overflow-hidden bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-800">
                <div
                    className="absolute inset-0 opacity-[0.06] pointer-events-none"
                    style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "22px 22px" }}
                />

                <div className="relative animate-fadeIn">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex items-center gap-3.5 min-w-0">
                            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex-shrink-0 overflow-hidden bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                                {userProfile?.avatar_url ? (
                                    <img src={userProfile.avatar_url} alt={firstName} className="w-full h-full object-cover" />
                                ) : (
                                    <span className="text-base font-bold text-amber-300 select-none">
                                        {(userProfile?.full_name || userProfile?.username || "?")[0].toUpperCase()}
                                    </span>
                                )}
                            </div>

                            <div className="min-w-0">
                                <h1 className="text-xl sm:text-3xl font-bold text-white tracking-tight truncate">
                                    {getGreeting()}, {firstName}
                                </h1>

                                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
                                    <p className="text-xs sm:text-sm text-zinc-400">
                                        {new Date().toLocaleDateString("en-AU", {
                                            weekday: "short",
                                            day: "numeric",
                                            month: "short",
                                            year: "numeric",
                                        })}
                                    </p>

                                    {company?.name && (
                                        <>
                                            <span className="text-zinc-700 hidden sm:inline">·</span>
                                            <span className="text-xs sm:text-sm text-zinc-400 truncate">{company.name}</span>
                                        </>
                                    )}

                                    {company?.subscription_plan === "enterprise" && (
                                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                                            Enterprise
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2.5">
                            <LivePill />

                            <button
                                onClick={() => loadStats(true)}
                                disabled={loading || refreshing}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10 hover:text-white text-xs font-medium transition-colors disabled:opacity-40"
                            >
                                <FiRefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                                {relativeTime ? `Updated ${relativeTime}` : "Refresh"}
                            </button>
                        </div>
                    </div>

                    <div className="mt-7 grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
                        <HeroStat label="Active Jobs" value={stats.activeJobs} Icon={FaBoxOpen} loading={loading} />
                        <HeroStat label="Urgent" value={stats.urgentJobsCount} Icon={FiZap} accent="text-red-400" loading={loading} />
                        <HeroStat label="Due This Week" value={stats.dueThisWeek} Icon={FiCalendar} loading={loading} />
                        <HeroStat
                            label="Pipeline Value"
                            value={formatMoneyCompact(stats.pipelineValue)}
                            Icon={FiDollarSign}
                            isText
                            loading={loading}
                        />
                    </div>
                </div>
            </div>

            {/* ── Quick Actions ─────────────────────────────────────────────── */}
            <div className="beta-quick-actions flex flex-wrap items-center justify-end gap-2">
                {ACTIONS_VISIBLE.map(({ id, route, Icon, label }) => (
                    <QuickActionPill key={id} Icon={Icon} label={label} onClick={() => go(route)} />
                ))}
            </div>

            {/* ── Overview ─────────────────────────────────────────────────── */}
            <div>
                <SectionLabel>Overview</SectionLabel>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatCard label="Completed" value={stats.completedJobs} Icon={FiCheckCircle} loading={loading} />
                    <StatCard label="Team" value={stats.totalUsers} Icon={MdManageAccounts} loading={loading} />
                    <StatCard label="Fully Priced" value={stats.fullyPricedJobs} Icon={FiDollarSign} tone="emerald" loading={loading} />
                    <StatCard label="Missing Pricing" value={stats.missingPricingJobs} Icon={FiAlertTriangle} tone="amber" loading={loading} />
                    <StatCard label="Not Yet Quoted" value={stats.notYetQuotedJobs} Icon={FiSend} tone="blue" loading={loading} />
                    <StatCard label="New This Week" value={stats.thisWeekJobs} Icon={FiTrendingUp} loading={loading} />
                </div>
            </div>

            {/* ── Pipeline + Needs Attention ───────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-3.5 border-b border-gray-100">
                        <p className="text-[15px] font-bold text-zinc-700 leading-tight">Job Pipeline</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">Active jobs by status</p>
                    </div>
                    <div className="p-5 space-y-4">
                        {loading ? (
                            [...Array(4)].map((_, i) => (
                                <div key={i} className="space-y-1.5">
                                    <Skeleton height={12} width="60%" />
                                    <Skeleton height={6} borderRadius={999} />
                                </div>
                            ))
                        ) : (stats.statusBreakdown || []).every((s) => s.count === 0) ? (
                            <div className="py-6 text-center text-sm text-gray-400">No active jobs</div>
                        ) : (
                            (stats.statusBreakdown || []).map(({ key, label, count }) => {
                                const meta = STATUS_META[key];
                                const pct = Math.round((count / maxBreakdownCount) * 100);
                                return (
                                    <div key={key}>
                                        <div className="flex items-center justify-between mb-1.5">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${meta?.dot}`} />
                                                <span className="text-xs text-gray-600">{label}</span>
                                            </div>
                                            <span className="text-xs font-semibold text-zinc-700 tabular-nums">{count}</span>
                                        </div>
                                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${meta?.bar} transition-all duration-500`}
                                                style={{ width: count === 0 ? "0%" : `${Math.max(pct, 3)}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                    {!loading && (stats.activeJobs ?? 0) > 0 && (
                        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                            <span className="text-xs text-gray-400">Total active</span>
                            <span className="text-xs font-semibold text-zinc-700 tabular-nums">{stats.activeJobs}</span>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
                    <div className="px-5 py-3.5 border-b border-gray-100">
                        <p className="text-[15px] font-bold text-zinc-700 leading-tight">Needs Attention</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">Jobs and pricing that need action</p>
                    </div>
                    <div className="p-4 space-y-2 flex-1">
                        {loading ? (
                            [...Array(3)].map((_, i) => <Skeleton key={i} height={40} borderRadius={8} className="block" />)
                        ) : !hasAttentionItems ? (
                            <div className="py-8 text-center">
                                <FiCheckCircle className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                                <p className="text-xs font-medium text-gray-500">All clear — nothing needs attention</p>
                            </div>
                        ) : (
                            <>
                                {(stats.missingPricingJobs ?? 0) > 0 && (
                                    <button
                                        onClick={() => go("final-pricings")}
                                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-100 hover:bg-amber-100 transition-colors text-left"
                                    >
                                        <span className="text-xs font-medium text-amber-800">Missing final pricing</span>
                                        <span className="text-sm font-bold text-amber-700 tabular-nums">{stats.missingPricingJobs}</span>
                                    </button>
                                )}

                                {(stats.notYetQuotedJobs ?? 0) > 0 && (
                                    <button
                                        onClick={() => go("quotations")}
                                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-blue-50 border border-blue-100 hover:bg-blue-100 transition-colors text-left"
                                    >
                                        <span className="text-xs font-medium text-blue-800">Priced, not yet quoted</span>
                                        <span className="text-sm font-bold text-blue-700 tabular-nums">{stats.notYetQuotedJobs}</span>
                                    </button>
                                )}

                                {(stats.urgentJobsList || []).length > 0 && (
                                    <div className="pt-1">
                                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-1 mb-1.5">Urgent jobs</p>
                                        <div className="space-y-1">
                                            {stats.urgentJobsList.map((job) => (
                                                <button
                                                    key={job.id}
                                                    onClick={() => go(`assignment/${job.id}`)}
                                                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors text-left group"
                                                >
                                                    <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                                                    <span className="text-xs font-mono text-gray-400">#{job.unique_id}</span>
                                                    <span className="text-xs font-medium text-zinc-700 truncate flex-1 group-hover:text-red-700">
                                                        {job.customer_name || "—"}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Trend ─────────────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-[15px] font-bold text-zinc-700 leading-tight">Jobs Created</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">Last 14 days</p>
                    </div>
                    <FiBarChart2 className="w-4 h-4 text-gray-300" />
                </div>
                <div className="mt-5">
                    {loading ? <Skeleton height={48} /> : <Sparkline data={stats.jobsTrend || []} />}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400">Total</span>
                    <span className="text-sm font-bold text-zinc-700 tabular-nums">{loading ? "—" : trendTotal}</span>
                </div>
            </div>

            {/* ── Recent Activity ──────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <p className="text-[15px] font-bold text-zinc-700 leading-tight">Recent Activity</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">Live feed across the whole company</p>
                    </div>
                    <LivePill />
                </div>
                <div className="divide-y divide-gray-50 max-h-[420px] overflow-y-auto">
                    {loading ? (
                        [...Array(6)].map((_, i) => (
                            <div key={i} className="px-5 py-3">
                                <Skeleton height={14} width="70%" />
                            </div>
                        ))
                    ) : (stats.recentActivity || []).length === 0 ? (
                        <div className="px-5 py-10 text-center text-xs text-gray-400">No activity yet</div>
                    ) : (
                        stats.recentActivity.map((row) => (
                            <div key={row.id} className="px-5 py-3 flex items-start gap-3">
                                <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500 flex-shrink-0 mt-0.5">
                                    {(row.user_name || "?")[0]?.toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs text-zinc-700">
                                        <span className="font-semibold">{row.user_name || "Someone"}</span>{" "}
                                        <span className="text-gray-500">{actionLabel(row.action)}</span>
                                    </p>
                                    {row.details && <p className="text-[11px] text-gray-400 mt-0.5 truncate">{row.details}</p>}
                                </div>
                                <span className="text-[10px] text-gray-300 flex-shrink-0 mt-0.5">{timeAgo(row.timestamp)}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* ── Recent Jobs ──────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <p className="text-[15px] font-bold text-zinc-700 leading-tight">Recent Jobs</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">Latest jobs created</p>
                    </div>
                    {hasPermission(userProfile?.role, ["super_admin", "admin", "designer", "estimator"]) && (
                        <button
                            onClick={() => go("assignment/list")}
                            className="flex items-center gap-1 text-xs font-semibold text-zinc-700 hover:text-zinc-900 transition-colors"
                        >
                            View all <FiArrowRight className="w-3 h-3" />
                        </button>
                    )}
                </div>

                <div className="divide-y divide-gray-50">
                    {loading ? (
                        [...Array(5)].map((_, i) => (
                            <div key={i} className="px-5 py-3.5">
                                <Skeleton height={14} width="60%" />
                            </div>
                        ))
                    ) : (stats.recentJobs || []).length === 0 ? (
                        <div className="px-5 py-12 text-center">
                            <p className="text-sm font-semibold text-gray-500 mb-1">No jobs yet</p>
                            <p className="text-xs text-gray-400">Jobs will appear here once created</p>
                        </div>
                    ) : (
                        stats.recentJobs.map((job) => {
                            const meta = STATUS_META[job.main_status];
                            const isUrgent = job.urgent_design || job.urgent_quote;

                            return (
                                <button
                                    key={job.id}
                                    onClick={() => go(`assignment/${job.id}`)}
                                    className="w-full px-5 py-3 grid grid-cols-[auto_1fr_auto_auto] gap-3 items-center hover:bg-gray-50 transition-colors text-left group"
                                >
                                    <div className="flex items-center gap-1.5 w-16">
                                        {isUrgent && <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />}
                                        <span className="font-mono text-xs text-gray-400 truncate">#{job.unique_id}</span>
                                    </div>
                                    <span className="text-sm font-semibold text-zinc-700 truncate group-hover:text-zinc-900 transition-colors">
                                        {job.customer_name || "—"}
                                    </span>
                                    <span className="text-[11px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-md flex-shrink-0 hidden sm:block">
                                        {JOB_TYPE_LABEL[job.job_type] || job.job_type || "—"}
                                    </span>
                                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${meta ? `${meta.bg} ${meta.text}` : "bg-gray-100 text-gray-500"}`}>
                                        {meta?.label || capitalize((job.main_status || "").replace(/_/g, " "))}
                                    </span>
                                </button>
                            );
                        })
                    )}
                </div>
            </div>

        </div>
    );
};

export default AdminDashboardBeta;
